import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'

export type NavBarVariant = 'default' | 'transparent' | 'inverse'

/**
 * NavBar has no dedicated entry in the v2 component tokens; its variants map to
 * semantic tokens directly (the same values they referenced in v1).
 */
const navBarColors: Record<
  NavBarVariant,
  {background: string; content: string}
> = {
  default: {
    background: cssTheme.colors.background.neutral.primary,
    content: cssTheme.colors.content.neutral.primary
  },
  transparent: {
    background: '#00000000',
    content: cssTheme.colors.content.neutral.primary
  },
  inverse: {
    background: cssTheme.colors.background.brand.inverse,
    content: cssTheme.colors.content.neutral.inverse
  }
}

type NavBarElementProps = {
  wrapCenterContent: boolean
  centerAligned: boolean
  variant: NavBarVariant
  withDivider: boolean
  hasCenterContent: boolean
  hasBottomContent: boolean
  hasPrimaryRowContent: boolean
  collapsiblePrimaryRow: boolean
}

const TRANSITION_DURATION = '0.3s'

/**
 * Bottom padding only applies when there is an actual second row: a center
 * content that wraps onto its own line, or a bottom content. For a single-row
 * navbar — including one with `wrapCenterContent` set but no center content to
 * wrap (e.g. only start/end content) — it collapses to 0 so the primary row
 * sits flush with the bottom edge.
 */
const bottomPadding =
  (value: string) =>
  ({
    wrapCenterContent,
    hasCenterContent,
    hasBottomContent
  }: NavBarElementProps) =>
    (wrapCenterContent && hasCenterContent) || hasBottomContent ? value : '0'

/**
 * Height of the first (primary) row, exposed as a custom property so it can be
 * tuned per breakpoint while the grid templates stay shared. By default the
 * track is `minmax(var(...), auto)`, keeping row 1 at least this tall while
 * still growing with its content. With `collapsiblePrimaryRow` the track
 * becomes a plain `var(...)` instead — a fixed (so animatable) height a
 * consumer can transition to 0 to collapse the row, at the cost of the
 * content-driven grow. Either way every other row stays purely content-sized.
 */
const PRIMARY_ROW_MIN_HEIGHT = '--nav-bar-primary-row-min-height'

const primaryRowTrack = (collapsible: boolean) =>
  collapsible
    ? `var(${PRIMARY_ROW_MIN_HEIGHT})`
    : `minmax(var(${PRIMARY_ROW_MIN_HEIGHT}), auto)`

/**
 * Height in px of a single-row NavBar at mobile breakpoints (and at every
 * breakpoint with `isCompact`). Exposed for layouts that must size content
 * against the bar (e.g. overlay sections using `calc(100% - <height>px)`).
 * Replaces the legacy `MOBILE_HEADER_HEIGHT` from `CompactHeader`.
 */
export const NAV_BAR_MOBILE_HEIGHT = 64

export const PRIMARY_ROW_MIN_HEIGHT_MOBILE = `${NAV_BAR_MOBILE_HEIGHT}px`
export const PRIMARY_ROW_MIN_HEIGHT_DESKTOP_XS = '82px'

/**
 * Inline layout: `start | center | end` on row 1, `bottom` spanning row 2.
 **/
const inlineGrid = (centerAligned: boolean, primaryRow: string) => `
  grid-template-columns: ${
    // The side tracks use a content-based minimum (\`auto\`) rather than \`0\` so a
    // fixed-width side slot (e.g. a close button) is never crushed below its
    // width on narrow viewports — which would let it overflow onto the centered
    // content. When there is room, both 1fr shares still win, keeping the centre
    // slot symmetrically centred.
    centerAligned
      ? 'minmax(auto, 1fr) auto minmax(auto, 1fr)'
      : 'auto minmax(0, 1fr) auto'
  };
  grid-template-rows: ${primaryRow} auto;
  grid-template-areas:
    'navStart navCenter navEnd'
    'navBottom navBottom navBottom';
`

/**
 * Wrapped layout: `start | end` on row 1 (center column left empty), `center`
 * full-width on row 2, `bottom` full-width on row 3. Keeps the same 3-column
 * track as the inline layout so the start/end columns stay aligned.
 */
const wrappedGrid = (primaryRow: string) => `
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-rows: ${primaryRow} auto auto;
  grid-template-areas:
    'navStart . navEnd'
    'navCenter navCenter navCenter'
    'navBottom navBottom navBottom';
`

export const NavBarElement = styled.nav<NavBarElementProps>`
  display: grid;
  align-items: center;
  width: 100%;
  background-color: ${({variant}) => navBarColors[variant].background};
  color: ${({variant}) => navBarColors[variant].content};
  border-bottom: ${({withDivider}) =>
    withDivider
      ? `1px solid ${cssTheme.colors.border.neutral.secondary}`
      : 'none'};
  ${PRIMARY_ROW_MIN_HEIGHT}: ${PRIMARY_ROW_MIN_HEIGHT_MOBILE};
  padding: 0 ${cssTheme.layout.spacing.s400}
    ${bottomPadding(cssTheme.layout.spacing.s400)};

  ${linariaMq.desktopXs} {
    padding: 0 ${cssTheme.layout.spacing.s600}
      ${bottomPadding(cssTheme.layout.spacing.s500)};
    ${PRIMARY_ROW_MIN_HEIGHT}: ${PRIMARY_ROW_MIN_HEIGHT_DESKTOP_XS};
  }

  /** Pins the bar to the top of its positioned container, overlapping content. */
  &.--is-floating {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
  }

  /**
   * Forces the mobile height and paddings at every breakpoint. Class specificity
   * beats the breakpoint rules above, so the bar stays compact on desktop too.
   */
  &.--is-compact {
    ${PRIMARY_ROW_MIN_HEIGHT}: ${PRIMARY_ROW_MIN_HEIGHT_MOBILE};
    padding: 0 ${cssTheme.layout.spacing.s400}
      ${bottomPadding(cssTheme.layout.spacing.s400)};
  }
  /*
   * Opt-in: a fixed (animatable) primary-row track plus a transition, so a
   * consumer can collapse the row by driving ${PRIMARY_ROW_MIN_HEIGHT} to 0.
   */
  ${({collapsiblePrimaryRow}) =>
    collapsiblePrimaryRow &&
    `transition: grid-template-rows ${TRANSITION_DURATION};`}

  ${({
    wrapCenterContent,
    centerAligned,
    hasBottomContent,
    hasPrimaryRowContent,
    collapsiblePrimaryRow
  }) => {
    const primaryRow = primaryRowTrack(collapsiblePrimaryRow)
    return wrapCenterContent
      ? `
    ${wrappedGrid(primaryRow)}
    ${
      // With nothing in the start/end slots the primary row has no content, so
      // collapse it to 0 and let the wrapped center content sit flush to the top
      // instead of reserving an empty min-height row above it. Reverts at
      // desktopMd, where the layout goes inline and the center reclaims row 1.
      hasPrimaryRowContent ? '' : 'grid-template-rows: 0 auto auto;'
    }

    ${linariaMq.desktopMd} {
      ${inlineGrid(centerAligned, primaryRow)}
      padding-bottom: ${hasBottomContent ? cssTheme.layout.spacing.s500 : '0'};
    }
  `
      : inlineGrid(centerAligned, primaryRow)
  }}
`

/** Fixed width, sized by its content. */
export const StartContent = styled.div`
  grid-area: navStart;
  display: flex;
  align-items: center;
  margin-left: ${minus(cssTheme.layout.spacing.s250)};
  ${linariaMq.desktopXs} {
    margin-left: 0;
  }
  /* Compact mode keeps the mobile negative margin at every breakpoint. */
  .--is-compact & {
    margin-left: ${minus(cssTheme.layout.spacing.s250)};
  }
`

/** Responsive. Inline between start/end, or full-width on its own row when wrapped. */
export const CenterContent = styled.div`
  grid-area: navCenter;
  display: flex;
  align-items: center;
  min-width: 0;
`

/** Fixed width, sized by its content. */
export const EndContent = styled.div`
  grid-area: navEnd;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: ${minus(cssTheme.layout.spacing.s250)};
  ${linariaMq.desktopXs} {
    margin-right: 0;
  }
  /* Compact mode keeps the mobile negative margin at every breakpoint. */
  .--is-compact & {
    margin-right: ${minus(cssTheme.layout.spacing.s250)};
  }
`

/** Responsive, full-width row. */
export const BottomContent = styled.div<{wrapCenterContent: boolean}>`
  grid-area: navBottom;
  min-width: 0;
  /* When the center content wraps onto its own row, separate it from this bottom
   * row by s500. Reverts at desktopMd, where the layout is inline again. */
  ${({wrapCenterContent}) =>
    wrapCenterContent &&
    `
    margin-top: ${cssTheme.layout.spacing.s500};

    ${linariaMq.desktopMd} {
      margin-top: 0;
    }
  `}
`
